<template>
  <div class="question-node switcher-node"
       :class="{ selected: isSelected, collapsed: isCollapsed }"
       :style="collapsedStyle">
    <!-- HEADER -->
    <div class="node-header">
      <button @click="toggleCollapse" class="collapse-btn" title="Réduire / développer">
        <span class="caret" :class="{ rotated: isCollapsed }">▲</span>
      </button>

      <p class="question-text">
        <span class="textQuestioHeader">Score - {{ data.label || 'Switcher' }}</span>
        <span v-if="isCollapsed && data.conditions?.length > 0" class="condition-badge">
          {{ data.conditions.length }}
        </span>
      </p>
    </div>

    <!-- TOOLBAR -->
    <div class="node-toolbar">
      <button class="toolbar-btn add" @click.stop="addCondition" title="Ajouter une condition">
        <i class="fas fa-plus"></i>
      </button>

      <button class="toolbar-btn delete" @click.stop="onDelete" title="Supprimer le nœud">
        <i class="fas fa-trash"></i>
      </button>
    </div>

    <!-- CONTENU -->
    <transition name="fade-slide">
      <div v-if="!isCollapsed" class="node-content">
        <div v-for="(cond, index) in data.conditions" :key="cond.id" class="condition-container">
          <div class="condition-row">
            <span class="condition-letter">{{ String.fromCharCode(65 + index) }}</span>
            <span class="operand-label">Score</span>

            <div class="condition-input">
              <select v-model="cond.operator" @change="onUpdate" class="form-select form-select-sm operator-select">
                <option value="==">=</option>
                <option value="!=">≠</option>
                <option value=">">></option>
                <option value="<"><</option>
                <option value=">=">>=</option>
                <option value="<="><=</option>
              </select>

              <input
                v-model="cond.value"
                @input="onUpdate"
                class="form-control form-control-sm value-input"
                placeholder="Valeur..."
              />
            </div>

            <button
              class="delete-condition-btn"
              title="Supprimer cette condition"
              @click.stop="removeCondition(cond.id)"
            >
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- HANDLE D’ENTRÉE -->
    <Handle
      id="input"
      type="target"
      :position="Position.Left"
      class="input-handle"
      :is-valid-connection="isValidConnection"
    />

    <!-- HANDLES DE SORTIE : un par condition -->
    <div class="connection-handles">
      <Handle
        v-for="(cond, index) in data.conditions"
        :key="cond.id"
        :id="cond.id"
        type="source"
        :position="Position.Right"
        :style="{ top: getHandlePosition(index) }"
        class="option-handle"
        :class="{ collapsed: isCollapsed }"
      >
        <span class="handle-label">{{ String.fromCharCode(65 + index) }}</span>
      </Handle>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, nextTick, computed } from 'vue'
import { Handle, Position, useVueFlow } from '@vue-flow/core'
import { v4 as uuidv4 } from 'uuid'
import { useTestStore } from '../../../stores/testStore'

const { getNodes } = useVueFlow()
const testStore = useTestStore()

/**
 * Empêche les nodes "options" (bleus) de se connecter au Switcher.
 * Autorise uniquement les nodes verts ("score").
 */
function isValidConnection(connection) {
  const nodes = getNodes()
  const source = nodes.find(n => n.id === connection.source)
  const sourceMode = source?.data?.mode
  return sourceMode === 'score'
}

const props = defineProps({
  id: String,
  data: { type: Object, default: () => ({ conditions: [] }) },
  selected: Boolean
})
const emit = defineEmits(['update', 'delete'])

// --- ÉTAT LOCAL
const isCollapsed = ref(props.data?.collapsed ?? false)
watch(() => props.data?.collapsed, (val) => (isCollapsed.value = !!val))

const toggleCollapse = async (event) => {
  event.stopPropagation()
  event.preventDefault()
  isCollapsed.value = !isCollapsed.value
  emit('update', { id: props.id, data: { collapsed: isCollapsed.value } })
  await nextTick()
}

// --- GESTION DES CONDITIONS
function addCondition() {
  const newCond = {
    id: uuidv4(),
    operator: '==',
    value: ''
  }
  const updated = [...(props.data.conditions || []), newCond]

  // ✅ Mise à jour réactive de props.data
  if (props.data) {
    props.data.conditions = updated
  }

  // ✅ Émet l'update vers le parent avec le payload correct
  emit('update', { id: props.id, data: { ...props.data, conditions: updated } })

  console.log(`➕ Condition ajoutée au switcher ${props.id}`)
}

function removeCondition(condId) {
  const updated = (props.data.conditions || []).filter(c => c.id !== condId)

  // ✅ Mise à jour réactive de props.data
  if (props.data) {
    props.data.conditions = updated
  }

  // ✅ Émet l'update vers le parent
  emit('update', { id: props.id, data: { ...props.data, conditions: updated } })

  console.log(`➖ Condition supprimée du switcher ${props.id}`)
}

function onUpdate() {
  // ✅ Émet l'update vers le parent (pas besoin de testStore ici)
  emit('update', { id: props.id, data: { ...props.data } })

  console.log(`📝 Switcher ${props.id} mis à jour`)
}

function onDelete() {
  if (window.confirm('⚠️ Are you sure you want to delete this node? \nThis action cannot be undone.')) {
    emit('delete', { nodeId: props.id, elementId: props.data?.elementId })
  }
}

// --- Sélection
const isSelected = ref(false)
watch(() => props.selected, (v) => (isSelected.value = v))

// --- Style dynamique pour la hauteur en mode collapsed
const collapsedStyle = computed(() => {
  if (!isCollapsed.value) return {}

  const conditionsCount = props.data.conditions?.length || 0
  if (conditionsCount === 0) return {}

  // Calcul de la hauteur minimale pour accommoder tous les handles
  const baseHeight = 50 // Hauteur du header
  const handleSpacing = 12
  const minHeightForHandles = baseHeight + (conditionsCount - 1) * handleSpacing + 10

  return {
    minHeight: `${Math.max(60, minHeightForHandles)}px`
  }
})

// --- Position dynamique des handles
function getHandlePosition(index) {
  if (isCollapsed.value) {
    // Mode collapsed : handles plus serrés et centrés
    const totalConditions = props.data.conditions?.length || 1
    const baseOffset = 30 // Position de départ plus haute
    const spacing = 12 // Espacement réduit entre handles
    return `${baseOffset + index * spacing}px`
  } else {
    // Mode étendu : position normale
    const baseOffset = 60
    const spacing = 35
    return `${baseOffset + index * spacing}px`
  }
}
</script>

<style scoped>
.question-node {
  background: white;
  border: 2px solid #ddd;
  border-radius: 8px;
  min-width: 300px;
  max-width: 420px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  position: relative;
  transition: all 0.3s ease;
}

.question-node.collapsed {
  min-width: 220px;
  max-width: 260px;
}

.question-node.selected {
  border-color: #0dfd21;
  box-shadow: 0 4px 12px rgba(13, 110, 253, 0.3);
  transform: translateY(-2px);
}

.node-header {
  background-color: #e6f8f1;
  padding: 10px;
  border-bottom: 1px solid #ddd;
  border-radius: 6px 6px 0 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.collapse-btn {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 12px;
  color: #666;
  padding: 4px 8px;
  border-radius: 3px;
  transition: all 0.2s ease;
}

.collapse-btn:hover {
  color: #0d6efd;
}

.caret {
  display: inline-block;
  transition: transform 0.25s ease;
  font-size: 12px;
}

.caret.rotated {
  transform: rotate(180deg);
}
.node-toolbar {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
  margin-bottom: 4px;
  margin-right: 20px;
}
.toolbar-btn {
  border: none;
  background: #f8f9fa;
  color: #555;
  width: 26px;
  height: 26px;
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
}
.toolbar-btn:hover {
  background: #e9ecef;
  color: #0d6efd;
  transform: translateY(-1px);
}
.node-content {
  padding: 10px 15px 15px;
}

.condition-container {
  background: #f8f9fa;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 8px;
  margin-bottom: 8px;
  transition: all 0.2s ease;
}

.condition-container:hover {
  background: #ffffff;
  border-color: #0d6efd;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
}

.condition-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.condition-letter {
  font-weight: bold;
  font-size: 13px;
  color: #0d6efd;
  background: #e7f1ff;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
}

.operand-label {
  font-weight: 600;
  font-size: 11px;
  color: #6c757d;
  width: 45px;
  text-align: center;
  flex-shrink: 0;
}

.condition-input {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-grow: 1;
}

.operator-select {
  width: 65px;
}

.value-input {
  flex-grow: 1;
}

.delete-condition-btn {
  background: #fff;
  border: 1px solid #dee2e6;
  color: #dc3545;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  padding: 0;
}

.delete-condition-btn:hover {
  background: #dc3545;
  color: white;
  border-color: #dc3545;
  transform: scale(1.1);
}

.delete-condition-btn:active {
  transform: scale(0.95);
}
.connection-handles {
  position: absolute;
  right: 0;
  top: 0;
  height: 100%;
}
.option-handle {
  width: 22px !important;
  height: 22px !important;
  background-color: #0d6efd !important;
  border: 2px solid white !important;
  color: white;
  border-radius: 6px !important;
  display: flex !important;
  align-items: center;
  justify-content: center;
  transform: translateX(50%);
  cursor: crosshair;
  position: relative;
  transition: all 0.25s ease;
  font-size: 10px;
  font-weight: bold;
}

.option-handle:hover {
  background-color: #0b5ed7 !important;
  transform: translateX(50%) scale(1.15);
  z-index: 10;
}

.option-handle.collapsed {
  width: 18px !important;
  height: 18px !important;
  font-size: 8px;
  opacity: 0.9;
}

.option-handle.collapsed:hover {
  transform: translateX(50%) scale(1.2);
  opacity: 1;
}

.handle-label {
  pointer-events: none;
}
.input-handle {
  width: 22px !important;
  height: 22px !important;
  background-color: #83e76a !important;
  border: 2px solid white !important;
  border-radius: 6px !important;
  display: flex !important;
  align-items: center;
  justify-content: center;
  position: absolute;
  left: -12px;
  top: 50%;
  transform: translateY(-50%);
  cursor: crosshair;
  z-index: 5;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
  transition: transform 0.2s ease, background-color 0.2s ease;
}
.input-handle:hover {
  background-color: #045508 !important;
  transform: translateY(-50%) scale(1.1);
}
.textQuestioHeader {
  color: #0056b3;
}

.question-text {
  display: flex;
  align-items: center;
  gap: 6px;
}

.condition-badge {
  background: #0d6efd;
  color: white;
  font-size: 10px;
  font-weight: bold;
  padding: 2px 6px;
  border-radius: 10px;
  min-width: 18px;
  text-align: center;
}

/* Transition pour le contenu */
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.3s ease;
}

.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

.fade-slide-enter-to,
.fade-slide-leave-from {
  opacity: 1;
  transform: translateY(0);
}

.form-control-sm{
    max-width:60px;
}
</style>
